import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { ConfirmButton } from "@/components/ConfirmDialog";
import { Building2, Plus, MapPin, Users, UserRound, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const EMPTY = {
  name: "", code: "", city: "", state: "", address: "", contact: "", email: "",
  admin_name: "", admin_email: "", admin_password: "", admin_password_confirm: "",
};

export default function Branches() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");

  const load = () => api.get("/branches").then((r) => setBranches(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ ...EMPTY, name: b.name, code: b.code || "", city: b.city, state: b.state, address: b.address || "", contact: b.contact || "", email: b.email || "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/branches/${editing.id}`, { name: form.name, city: form.city, state: form.state, address: form.address, contact: form.contact, email: form.email });
        toast.success("Branch updated");
      } else {
        if (form.admin_email && form.admin_password !== form.admin_password_confirm) {
          return toast.error("Admin passwords do not match");
        }
        const { admin_password_confirm, ...payload } = form;
        await api.post("/branches", payload);
        toast.success("Branch created");
      }
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const toggle = async (b) => {
    try {
      await api.put(`/branches/${b.id}`, { active: !b.active });
      toast.success(b.active ? "Branch deactivated" : "Branch activated");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (b) => {
    try {
      await api.delete(`/branches/${b.id}`);
      toast.success("Branch deleted");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const filtered = branches.filter((b) =>
    (b.name + b.city + b.state).toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="animate-fade-up">
      <PageHeader title="Branches" subtitle="Manage all branch offices across the organization">
        <button onClick={openCreate} data-testid="add-branch-button" className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors">
          <Plus className="h-4 w-4" /> Add Branch
        </button>
      </PageHeader>

      <div className="relative mb-4 max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="branch-search-input" placeholder="Search branch, city or state…" className="w-full rounded-lg bg-secondary border border-input pl-10 pr-3 py-2.5 text-sm outline-none focus:border-primary transition-colors" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="card-surface max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Branch" : "Add Branch"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Branch Name" testid="branch-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            {!editing && <Field label="Branch ID / Code" testid="branch-code-input" value={form.code} onChange={(v) => setForm({ ...form, code: v })} />}
            <Field label="City" testid="branch-city-input" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
            <Field label="State" testid="branch-state-input" value={form.state} onChange={(v) => setForm({ ...form, state: v })} />
            <Field label="Contact Number" testid="branch-contact-input" value={form.contact} onChange={(v) => setForm({ ...form, contact: v })} />
            <Field label="Email" testid="branch-email-input" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <div className="col-span-2"><Field label="Address" testid="branch-address-input" value={form.address} onChange={(v) => setForm({ ...form, address: v })} /></div>
          </div>
          {!editing && (
            <div className="mt-2 border-t border-border pt-3">
              <div className="text-sm font-semibold mb-2">Branch Admin Login (optional)</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Admin Name" testid="branch-admin-name-input" value={form.admin_name} onChange={(v) => setForm({ ...form, admin_name: v })} />
                <Field label="Login ID / Email" testid="branch-admin-email-input" value={form.admin_email} onChange={(v) => setForm({ ...form, admin_email: v })} />
                <div className="col-span-2"><Field label="Password" type="password" testid="branch-admin-password-input" value={form.admin_password} onChange={(v) => setForm({ ...form, admin_password: v })} /></div>
                <div className="col-span-2"><Field label="Confirm Password" type="password" testid="branch-admin-password-confirm-input" value={form.admin_password_confirm} onChange={(v) => setForm({ ...form, admin_password_confirm: v })} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button data-testid="branch-save-button" onClick={save} className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors">{editing ? "Save Changes" : "Create Branch"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="branches-grid">
        {filtered.map((b) => (
          <div key={b.id} onClick={() => navigate(`/branches/${b.id}`)} className="card-surface rounded-xl p-5 stat-glow cursor-pointer" data-testid={`branch-card-${b.id}`}>
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
                <Building2 className="h-5 w-5" />
              </div>
              <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => toggle(b)} data-testid={`branch-toggle-${b.id}`} className={`text-xs px-2.5 py-1 rounded-full font-medium ${b.active ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}`}>
                  {b.active ? "Active" : "Inactive"}
                </button>
                <button onClick={() => openEdit(b)} data-testid={`branch-edit-${b.id}`} className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary row-hover">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                {user.role === "super_admin" && (
                  <ConfirmButton
                    title="Delete branch?"
                    description={`This permanently deletes "${b.name}". Branches with managers or clients cannot be deleted — reassign or archive them first.`}
                    confirmLabel="Delete Branch"
                    testid={`branch-delete-confirm-${b.id}`}
                    onConfirm={() => remove(b)}
                    trigger={
                      <button data-testid={`branch-delete-${b.id}`} className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 row-hover">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    }
                  />
                )}
              </div>
            </div>
            <div className="mt-4 font-display font-semibold text-lg">{b.name}</div>
            <div className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
              <MapPin className="h-3.5 w-3.5" /> {b.city}, {b.state} · <span className="num">{b.code}</span>
            </div>
            <div className="mt-4 pt-4 border-t border-border/60 flex gap-6" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => navigate(`/managers?branch=${b.id}`)} data-testid={`branch-managers-link-${b.id}`} className="flex items-center gap-2 text-sm row-hover hover:text-primary">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="num font-medium">{b.manager_count}</span>
                <span className="text-xs text-muted-foreground">Managers</span>
              </button>
              <button onClick={() => navigate(`/clients?branch=${b.id}`)} data-testid={`branch-clients-link-${b.id}`} className="flex items-center gap-2 text-sm row-hover hover:text-primary">
                <UserRound className="h-4 w-4 text-muted-foreground" />
                <span className="num font-medium">{b.client_count}</span>
                <span className="text-xs text-muted-foreground">Clients</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input
        type={type}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm outline-none focus:border-primary transition-colors"
      />
    </div>
  );
}
